import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { notification } from 'antd';
import { FiAlertCircle, FiCheck, FiInfo, FiShield } from 'react-icons/fi';
import { updateStore } from 'features/placeSlice';
import PropertyService from 'service/PropertyService';
import { HOME_PAGE } from 'settings/constant';
import { isAdminUser } from 'library/utils/isAdmin';
import {
  OnboardingShell,
  Chip,
  ChipRow,
  Field,
  OutlineButton,
  PrimaryButton,
  TextInput,
  IMPORT_SECTIONS,
  ob,
} from 'components/Onboarding';
import SaveListingLoginModal from './SaveListingLoginModal';
import VerifyImport from './VerifyImport';
import MissingDetails from './MissingDetails';

const IMPORT_RAIL = IMPORT_SECTIONS;

const isAirbnbUrl = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    // Covers all regional Airbnb TLDs plus Airbnb's own short-link domain (abnb.me).
    return hostname.includes('airbnb.') || hostname === 'abnb.me' || hostname.endsWith('.abnb.me');
  } catch (_) {
    return false;
  }
};

const isHostProfileUrl = (value: string): boolean => /\/users\/|\/host(s)?\/|profile/i.test(value);

const JOB_KEY = 'airbnbImportJobId';
/** A logged-out visitor's link, kept while they log in (or sign up) so it can be submitted after. */
const PENDING_URL_KEY = 'pendingImportUrl';
/** "List it myself" chosen before logging in: carried on to the manual flow once they are in. */
const PENDING_MANUAL_KEY = 'pendingManualListing';
const POLL_MS = 2000;

const isLoggedIn = (): boolean => {
  try {
    return !!localStorage.getItem('jwtToken');
  } catch (_) {
    return false;
  }
};

const readPendingUrl = (): string => {
  try {
    return localStorage.getItem(PENDING_URL_KEY) || '';
  } catch (_) {
    return '';
  }
};

const hasPendingManual = (): boolean => {
  try {
    return localStorage.getItem(PENDING_MANUAL_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const setPendingManual = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(PENDING_MANUAL_KEY, '1');
    else localStorage.removeItem(PENDING_MANUAL_KEY);
  } catch (_) {
    /* storage unavailable */
  }
};

const setPendingUrl = (url: string | null): void => {
  try {
    if (url) localStorage.setItem(PENDING_URL_KEY, url);
    else localStorage.removeItem(PENDING_URL_KEY);
  } catch (_) {
    /* storage unavailable */
  }
};

interface ImportStep {
  key: string;
  label: string;
  state: 'done' | 'running' | 'pending' | 'failed';
}

interface ImportJob {
  jobId: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILED';
  percent: number;
  steps: ImportStep[];
  imagesDone?: number | null;
  imagesTotal?: number | null;
  error?: string | null;
  propertyId?: number | null;
}

const isActive = (job: ImportJob | null): boolean =>
  !!job && (job.status === 'QUEUED' || job.status === 'RUNNING');

type UrlState = 'empty' | 'invalid' | 'profile' | 'listing';

const readUrl = (value: string): UrlState => {
  const trimmed = value.trim();
  if (!trimmed) return 'empty';
  try {
    const parsed = new URL(trimmed);
    if (!/^https?:$/.test(parsed.protocol) || !parsed.hostname.includes('.')) return 'invalid';
  } catch (_) {
    return 'invalid';
  }
  return isHostProfileUrl(trimmed) ? 'profile' : 'listing';
};

/** "Let Us List It For You": the host shares a link and our team brings the listing over. */
const ImportListing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = isAdminUser();

  const [listingUrl, setListingUrl] = useState('');
  const [businessType, setBusinessType] = useState('B2C');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [job, setJob] = useState<ImportJob | null>(null);
  // A visitor who is not logged in gets the login / sign-up sheet straight away; the link box and
  // "list it myself" only work once they are in. (Not when returning to an existing listing by id.)
  const [loggedIn, setLoggedIn] = useState<boolean>(() => isLoggedIn());
  const [showLogin, setShowLogin] = useState<boolean>(
    () => !isLoggedIn() && !new URLSearchParams(window.location.search).get('id'),
  );
  /** Set once a logged-out import is saved to the account: shows the "We found your property" review. */
  const [verifyId, setVerifyId] = useState<number | string | null>(null);
  /** The "What did we miss?" step that follows the review. */
  const [missingStep, setMissingStep] = useState(false);

  const urlState = readUrl(listingUrl);

  const clearJob = (): void => {
    try {
      localStorage.removeItem(JOB_KEY);
    } catch (_) {
      /* storage unavailable */
    }
    setJob(null);
  };

  /**
   * Sends the link in (the host is logged in by now). An admin keeps the live import with its review
   * steps; anyone else just gets the "link submitted" message while the import runs in the background
   * and the listing is sent for approval when it is ready.
   */
  const submitUrl = async (raw: string): Promise<void> => {
    const admin = isAdminUser();
    let hostId: string | number | null = null;
    try {
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) hostId = JSON.parse(userDetails)?.id ?? null;
    } catch (_) {
      hostId = null;
    }
    if (!hostId) {
      notification.error({
        message: 'Unable to proceed',
        description: 'Host details not found. Please log in again.',
      });
      return;
    }

    setSubmitting(true);
    try {
      if (isAirbnbUrl(raw)) {
        const started: ImportJob = await PropertyService.startAirbnbOnboarding({
          url: raw,
          ...(admin ? { businessType } : {}),
        });
        if (admin) {
          try {
            localStorage.setItem(JOB_KEY, started.jobId);
          } catch (_) {
            /* storage unavailable */
          }
          setJob(started);
          setListingUrl('');
          return;
        }
      } else {
        const response: any = await PropertyService.submitAssistedListing({
          url: raw,
          hostId: Number(hostId),
          ...(admin ? { businessType } : {}),
        });
        if (response?.success === false) {
          notification.error({
            message: 'Submission failed',
            description:
              response?.message || 'Could not submit your listing URL. Please try again.',
          });
          return;
        }
      }
      setListingUrl('');
      setSubmitted(true);
    } catch (error: any) {
      console.error('Listing link request failed:', error);
      notification.error({
        message: 'Something went wrong',
        description:
          error?.response?.data?.message || 'Could not submit your listing URL. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Pick things back up after a refresh, a sign-up detour, or when the host comes back later.
  useEffect(() => {
    // Coming back from the plan screen: reopen the saved listing's review steps.
    const params = new URLSearchParams(window.location.search);
    const returnId = params.get('id');
    if (returnId && isLoggedIn()) {
      setVerifyId(returnId);
      setMissingStep(params.get('step') === 'missing');
      return;
    }
    // "List it myself" chosen before logging in or signing up: carry on now that they are in.
    if (hasPendingManual() && isLoggedIn()) {
      setPendingManual(false);
      startManually();
      return;
    }
    // A link entered before logging in or signing up: send it now that they are in.
    const pending = readPendingUrl();
    if (pending && isLoggedIn()) {
      setPendingUrl(null);
      submitUrl(pending);
      return;
    }
    let id: string | null = null;
    try {
      id = localStorage.getItem(JOB_KEY);
    } catch (_) {
      id = null;
    }
    if (!id || !isLoggedIn() || !isAdminUser()) return;
    PropertyService.getAirbnbOnboardingStatus(id)
      .then((previous: ImportJob) => {
        // Only an import that is still running is picked back up. A finished one is forgotten, so
        // starting "Let us list it for you" again shows the link box instead of the old result.
        if (isActive(previous)) {
          setJob(previous);
          return;
        }
        try {
          localStorage.removeItem(JOB_KEY);
        } catch (_) {
          /* storage unavailable */
        }
      })
      .catch(() => {
        try {
          localStorage.removeItem(JOB_KEY);
        } catch (_) {
          /* storage unavailable */
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep asking the server for progress while the import is running.
  const jobId = job?.jobId;
  const running = isActive(job);
  useEffect(() => {
    if (!jobId || !running) return undefined;
    const timer = window.setInterval(() => {
      PropertyService.getAirbnbOnboardingStatus(jobId)
        .then(setJob)
        .catch((error) => {
          if (error?.response?.status === 404) clearJob();
          // any other error is transient; the next tick tries again
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, running]);

  // A logged-in host's import just finished (they watched it run): carry straight on to the review.
  const sawRunning = useRef(false);
  useEffect(() => {
    if (running) sawRunning.current = true;
  }, [running]);
  const doneWithProperty =
    !!job && !!job.propertyId && (job.status === 'SUCCESS' || job.status === 'PARTIAL_SUCCESS');
  useEffect(() => {
    if (doneWithProperty && sawRunning.current && job?.propertyId) {
      setVerifyId(job.propertyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneWithProperty]);

  const goToSignUp = (phone: string): void => {
    localStorage.setItem('redirectAfterLogin', '/host/import-listing');
    navigate('/sign-up', { state: { phone } });
  };

  const startManually = (): void => {
    // Not logged in: log in (or sign up) first, then this flow starts.
    if (!isLoggedIn()) {
      setPendingManual(true);
      setShowLogin(true);
      return;
    }
    localStorage.removeItem('newPropertId');
    dispatch(updateStore(true)); // resets the onboarding store (the payload is ignored)
    navigate('/host/description');
  };

  const submit = async (): Promise<void> => {
    const raw = listingUrl.trim();
    if (urlState === 'empty' || urlState === 'invalid') {
      notification.warning({
        message: raw ? 'Invalid URL' : 'URL required',
        description: raw
          ? 'Please enter a valid listing URL.'
          : 'Please add your listing URL to continue.',
      });
      return;
    }
    // Not logged in: log in (or sign up) first; the link is sent as soon as they are in.
    if (!isLoggedIn()) {
      setPendingUrl(raw);
      setShowLogin(true);
      return;
    }
    setLoggedIn(true);
    await submitUrl(raw);
  };

  const goBack = (): void => navigate(HOME_PAGE, { state: { showListingOptions: true } });
  const goToDashboard = (): void => navigate(HOME_PAGE);

  if (verifyId) {
    return missingStep ? (
      <MissingDetails
        propertyId={verifyId}
        rail={IMPORT_RAIL}
        onBack={() => setMissingStep(false)}
      />
    ) : (
      <VerifyImport propertyId={verifyId} rail={IMPORT_RAIL} onNext={() => setMissingStep(true)} />
    );
  }

  const finished = !!job && !isActive(job);
  const failed = job?.status === 'FAILED';
  const inProgress = !!job;
  const percent = Math.max(0, Math.min(100, job?.percent ?? 0));

  const tryAgain = (): void => {
    clearJob();
  };

  const finish = (): void => {
    clearJob();
    goToDashboard();
  };

  return (
    <OnboardingShell
      section={inProgress || submitted ? 1 : 0}
      railItems={IMPORT_RAIL}
      title={
        inProgress
          ? failed
            ? 'We couldn’t finish your import'
            : finished
            ? 'Your listing is ready'
            : 'We’re building your listing'
          : submitted
          ? 'Your listing link has been submitted'
          : 'Bring your existing listing'
      }
      intro={
        inProgress || submitted
          ? undefined
          : 'Share a listing you already have and we’ll bring the details over for you.'
      }
      headerBack={goBack}
      onBack={goBack}
      onContinue={inProgress || submitted ? undefined : submit}
      continueLabel="Import my listing"
      continueLoading={submitting}
      continueDisabled={urlState !== 'listing' && urlState !== 'profile'}
      strip={
        inProgress || submitted
          ? undefined
          : { lead: 'I don’t have a link -', action: 'let me list it myself', onClick: startManually }
      }
      showImport={false}
      hideActions={inProgress || submitted}
    >
      {inProgress && job ? (
        <>
          <ProgressCard>
            <ProgressHead>
              <span>
                {failed
                  ? 'Import stopped'
                  : finished
                  ? 'Import complete'
                  : 'Importing your property'}
              </span>
              <b>{failed ? '' : `${percent}%`}</b>
            </ProgressHead>
            <Track role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
              <Fill $tone={failed ? 'bad' : 'ok'} style={{ width: `${failed ? 100 : percent}%` }} />
            </Track>
            <StepList>
              {job.steps.map((step) => (
                <StepRow key={step.key} $state={step.state}>
                  <StepIcon $state={step.state}>
                    {step.state === 'done' ? <FiCheck size={14} /> : null}
                    {step.state === 'failed' ? <FiAlertCircle size={14} /> : null}
                    {step.state === 'running' ? <Spinner /> : null}
                  </StepIcon>
                  <span className="label">
                    {step.label}
                    {step.key === 'UPLOAD_IMAGES' &&
                    step.state === 'running' &&
                    job.imagesTotal
                      ? ` (${job.imagesDone ?? 0}/${job.imagesTotal})`
                      : ''}
                  </span>
                  <span className="note">
                    {step.state === 'running'
                      ? 'Looking now…'
                      : step.state === 'failed'
                      ? 'Skipped'
                      : ''}
                  </span>
                </StepRow>
              ))}
            </StepList>
          </ProgressCard>

          {failed ? (
            <Detected $tone="bad" role="alert">
              <FiAlertCircle size={18} />{' '}
              {job.error || 'Something went wrong while importing your listing.'}
            </Detected>
          ) : null}
          {job.status === 'PARTIAL_SUCCESS' ? (
            <Detected $tone="warn" role="status">
              <FiAlertCircle size={18} /> Some details couldn’t be copied. Please review your
              listing and add anything that’s missing.
            </Detected>
          ) : null}
          {!finished ? (
            <Privacy>
              <FiShield size={18} /> This usually takes a minute or two. You can leave this page
              and come back — your import keeps running.
            </Privacy>
          ) : null}

          {finished ? (
            <Actions>
              {failed ? (
                <>
                  <OutlineButton type="button" onClick={tryAgain}>
                    Try another link
                  </OutlineButton>
                  <PrimaryButton type="button" onClick={startManually}>
                    List it myself
                  </PrimaryButton>
                </>
              ) : job.propertyId ? (
                <PrimaryButton type="button" onClick={() => setVerifyId(job.propertyId as number)}>
                  Review your listing
                </PrimaryButton>
              ) : (
                <PrimaryButton type="button" onClick={finish}>
                  Go to your listings
                </PrimaryButton>
              )}
            </Actions>
          ) : null}

        </>
      ) : submitted ? (
        <>
          <Success>
            <p className="t">Thanks! Our team will look into your link and set up your listing for you.</p>
            <p>
              We’ll notify you as soon as your listing is available. In the meantime, keep an eye on
              the <strong>Pending Approval</strong> tab on your dashboard — your listing will appear
              there once it’s created. Please refresh the page if you do not see your listing in the{' '}
              <strong>Pending Approval</strong> tab.
            </p>
          </Success>
          <Actions>
            <PrimaryButton type="button" onClick={goToDashboard}>
              Go to dashboard
            </PrimaryButton>
            <OutlineButton type="button" onClick={() => setSubmitted(false)}>
              Submit another link
            </OutlineButton>
          </Actions>
        </>
      ) : (
        <>
          {!loggedIn ? (
            <Detected $tone="warn" role="status">
              <FiInfo size={18} /> Log in or sign up to paste your listing link.{' '}
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  font: 'inherit',
                  fontWeight: 700,
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Log in / Sign up
              </button>
            </Detected>
          ) : null}
          <Field label="Listing URL" required>
            <TextInput
              type="url"
              inputMode="url"
              value={listingUrl}
              onChange={(e) => setListingUrl(e.target.value)}
              placeholder="https://your-listing-platform.com/your-listing"
              disabled={submitting || !loggedIn}
              autoComplete="off"
              spellCheck={false}
              $invalid={urlState === 'invalid'}
            />
          </Field>

          {urlState === 'listing' ? (
            <Detected $tone="ok" role="status">
              <FiCheck size={18} /> Single listing detected
            </Detected>
          ) : null}
          {urlState === 'profile' ? (
            <Detected $tone="warn" role="status">
              <FiAlertCircle size={18} /> This looks like a host profile link — a link to one
              listing works best
            </Detected>
          ) : null}
          {urlState === 'invalid' ? (
            <Detected $tone="bad" role="alert">
              <FiAlertCircle size={18} /> Enter a full link that starts with https://
            </Detected>
          ) : null}

          {isAdmin ? (
            <Field label="Property Type">
              <ChipRow>
                {['B2C', 'B2B'].map((type) => (
                  <Chip
                    key={type}
                    type="button"
                    $active={businessType === type}
                    aria-pressed={businessType === type}
                    onClick={() => setBusinessType(type)}
                  >
                    {businessType === type ? <FiCheck size={14} /> : null}
                    {type === 'B2C' ? 'B2C (Consumer)' : 'B2B (Business)'}
                  </Chip>
                ))}
              </ChipRow>
            </Field>
          ) : null}

          <Privacy>
            <FiInfo size={18} /> You can submit only one listing link at a time. To import another
            listing, submit its link again once this one is done.
          </Privacy>

          <Privacy>
            <FiShield size={18} /> We only access information visible on the public page.
          </Privacy>

          {showLogin && !isLoggedIn() ? (
            <SaveListingLoginModal
              intro="Log in or sign up to list your property."
              onClose={() => {
                setShowLogin(false);
                setPendingUrl(null);
                setPendingManual(false);
              }}
              onLoggedIn={() => {
                setShowLogin(false);
                setLoggedIn(true);
                const pending = readPendingUrl();
                setPendingUrl(null);
                if (hasPendingManual()) {
                  setPendingManual(false);
                  startManually();
                  return;
                }
                if (pending) submitUrl(pending);
              }}
              onNeedSignUp={goToSignUp}
            />
          ) : null}
        </>
      )}
    </OnboardingShell>
  );
};

const Detected = styled.div<{ $tone: 'ok' | 'warn' | 'bad' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'ok' ? 'rgba(46,125,50,0.25)' : $tone === 'warn' ? 'rgba(180,83,9,0.25)' : 'rgba(190,18,60,0.25)'};
  border-radius: 14px;
  background: ${({ $tone }) =>
    $tone === 'ok' ? ob.successSoft : $tone === 'warn' ? ob.warningSoft : 'rgba(190,18,60,0.06)'};
  color: ${({ $tone }) => ($tone === 'ok' ? ob.success : $tone === 'warn' ? ob.warning : ob.danger)};
  font-size: 15px;
  font-weight: 600;
  svg {
    flex-shrink: 0;
  }
`;

const Privacy = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 14.5px;
  color: ${ob.muted};
  svg {
    flex-shrink: 0;
    color: ${ob.success};
  }
`;

const Success = styled.div`
  padding: 18px 20px;
  border: 1px solid #9ae6b4;
  border-left: 3px solid ${ob.success};
  border-radius: 14px;
  background: ${ob.successSoft};
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.65;
    color: #2f855a;
  }
  .t {
    margin-bottom: 6px;
    font-size: 16px;
    font-weight: 700;
    color: #276749;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ProgressCard = styled.div`
  padding: 22px;
  border: 1px solid ${ob.border};
  border-radius: 18px;
  background: ${ob.surface};
`;

const ProgressHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  b {
    font-size: 18px;
    color: ${ob.primary};
  }
`;

const Track = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

const Fill = styled.div<{ $tone: 'ok' | 'bad' }>`
  height: 100%;
  border-radius: 999px;
  background: ${({ $tone }) => ($tone === 'bad' ? ob.danger : ob.primary)};
  transition: width 0.6s ease;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepRow = styled.li<{ $state: ImportStep['state'] }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: ${({ $state }) => ($state === 'pending' ? ob.muted : 'inherit')};
  .label {
    flex: 1;
    font-weight: ${({ $state }) => ($state === 'running' ? 600 : 500)};
  }
  .note {
    font-size: 13px;
    color: ${ob.muted};
  }
`;

const StepIcon = styled.span<{ $state: ImportStep['state'] }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $state }) => ($state === 'done' ? ob.success : $state === 'failed' ? ob.warning : $state === 'running' ? ob.primary : 'rgba(0,0,0,0.18)')};
  background: ${({ $state }) => ($state === 'done' ? ob.success : 'transparent')};
  color: ${({ $state }) => ($state === 'done' ? '#fff' : ob.warning)};
`;

const Spinner = styled.span`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: ${ob.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export default ImportListing;
